import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, ExternalLink, Save } from "lucide-react";
import { crm, getMongoLeads } from "./crmApi";
import { Table, Pill, statusTone, Modal, Field, Area, Sel, Loading, Err, fmtDate } from "./ui";
import { Button } from "@/components/ui/Button";

const LEAD_STATUSES = ["New", "Contacted", "Estimate Sent", "Converted", "Lost"];
const FOLLOWUP_STATUSES = ["Pending", "Done"];

// ---------- LEADS ----------
export const LeadsTab = () => {
  const [leads, setLeads] = useState(null);
  const [mongo, setMongo] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const r = await crm("listLeads");
      setLeads(r.leads.reverse());
    } catch (e) { setError(e); setLeads([]); }
    try { setMongo(await getMongoLeads()); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const patch = async (leadId, p) => {
    try {
      await crm("updateLead", { leadId, patch: p });
      toast.success("Lead updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (leads === null) return <Loading />;
  return (
    <div className="space-y-8">
      {error && <Err error={error} />}
      {!error && (
        <Table
          testId="crm-leads-table"
          cols={[
            { key: "Lead ID", label: "Lead" },
            { key: "Date Submitted", label: "Date", render: r => fmtDate(r["Date Submitted"]) },
            { key: "Customer ID", label: "Customer" },
            { key: "Requested Service", label: "Service" },
            { key: "Job Description", label: "Description", render: r => <span className="line-clamp-2 max-w-[260px] inline-block">{r["Job Description"]}</span> },
            { key: "Uploaded Photos", label: "Photos", render: r => r["Uploaded Photos"] ? String(r["Uploaded Photos"]).split("\n").filter(Boolean).length : 0 },
            { key: "Lead Status", label: "Status", render: r => (
              <span onClick={e => e.stopPropagation()}>
                <Sel label="" options={LEAD_STATUSES} value={r["Lead Status"]}
                  onChange={e => patch(r["Lead ID"], { "Lead Status": e.target.value })} />
              </span>
            )},
            { key: "Follow-Up Status", label: "Follow-Up", render: r => (
              <span onClick={e => e.stopPropagation()}>
                <Sel label="" options={FOLLOWUP_STATUSES} value={r["Follow-Up Status"]}
                  onChange={e => patch(r["Lead ID"], { "Follow-Up Status": e.target.value })} />
              </span>
            )},
          ]}
          rows={leads}
        />
      )}

      <div>
        <h3 className="font-display text-xl text-white mb-3">Website Inbox <span className="text-white/40 text-sm">(backup copy of every form submission)</span></h3>
        <Table
          testId="mongo-leads-table"
          cols={[
            { key: "created_at", label: "Date", render: r => fmtDate(r.created_at) },
            { key: "name", label: "Name" },
            { key: "phone", label: "Phone", render: r => <a className="text-weld-glow hover:underline" href={`tel:${r.phone}`}>{r.phone}</a> },
            { key: "service", label: "Service" },
            { key: "photos", label: "Photos", render: r => (r.photos || []).length },
            { key: "forwarded", label: "In CRM", render: r => <Pill tone={r.forwarded ? "green" : "amber"}>{r.forwarded ? "Synced" : "Pending"}</Pill> },
          ]}
          rows={mongo}
        />
      </div>
    </div>
  );
};

// ---------- CUSTOMERS ----------
const EMPTY_CUST = { name: "", phone: "", email: "", address: "", serviceArea: "", notes: "" };

export const CustomersTab = () => {
  const [customers, setCustomers] = useState(null);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_CUST);
  const [profile, setProfile] = useState(null); // getCustomer result
  const [notes, setNotes] = useState("");

  const load = async () => {
    setError(null);
    try { const r = await crm("listCustomers"); setCustomers(r.customers.reverse()); }
    catch (e) { setError(e); setCustomers([]); }
  };
  useEffect(() => { load(); }, []);

  const openProfile = async (c) => {
    try {
      const r = await crm("getCustomer", { customerId: c["Customer ID"] });
      setProfile(r);
      setNotes(r.customer["Notes"] || "");
    } catch (e) { toast.error(e.message); }
  };

  const addCustomer = async (e) => {
    e.preventDefault();
    try {
      await crm("addCustomer", { ...form, source: "manual" });
      toast.success("Customer added");
      setShowAdd(false); setForm(EMPTY_CUST); load();
    } catch (err) { toast.error(err.message); }
  };

  const saveNotes = async () => {
    try {
      await crm("updateCustomer", { customerId: profile.customer["Customer ID"], patch: { Notes: notes } });
      toast.success("Notes saved");
    } catch (e) { toast.error(e.message); }
  };

  if (customers === null) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="add-customer-btn" size="sm" onClick={() => setShowAdd(true)}><UserPlus size={16} /> Add Customer</Button>
      </div>
      {error ? <Err error={error} /> : (
        <Table
          testId="customers-table"
          onRowClick={openProfile}
          cols={[
            { key: "Customer ID", label: "ID" },
            { key: "name", label: "Name", render: r => `${r["First Name"]} ${r["Last Name"]}`.trim() },
            { key: "Phone", label: "Phone", render: r => <a className="text-weld-glow hover:underline" href={`tel:${r["Phone"]}`} onClick={e => e.stopPropagation()}>{r["Phone"]}</a> },
            { key: "Email", label: "Email" },
            { key: "Service Area", label: "Area" },
            { key: "Customer Status", label: "Status", render: r => <Pill tone={statusTone(r["Customer Status"])}>{r["Customer Status"]}</Pill> },
            { key: "Date Created", label: "Since", render: r => fmtDate(r["Date Created"]) },
          ]}
          rows={customers}
        />
      )}

      {showAdd && (
        <Modal title="Add Customer" onClose={() => setShowAdd(false)} testId="add-customer-modal">
          <form onSubmit={addCustomer} className="space-y-4">
            <Field label="Full Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <Field label="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <Field label="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            <Field label="Service Area" value={form.serviceArea} onChange={e => setForm({ ...form, serviceArea: e.target.value })} />
            <Area label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Button data-testid="add-customer-submit" type="submit" className="w-full">Save Customer</Button>
          </form>
        </Modal>
      )}

      {profile && (
        <Modal title={`${profile.customer["First Name"]} ${profile.customer["Last Name"]}`.trim() || profile.customer["Customer ID"]} onClose={() => setProfile(null)} testId="customer-profile">
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-3 text-white/75">
              <div><span className="text-white/40">ID:</span> {profile.customer["Customer ID"]}</div>
              <div><span className="text-white/40">Phone:</span> <a className="text-weld-glow" href={`tel:${profile.customer["Phone"]}`}>{profile.customer["Phone"]}</a></div>
              <div className="col-span-2"><span className="text-white/40">Email:</span> {profile.customer["Email"] || "—"}</div>
              <div className="col-span-2"><span className="text-white/40">Address:</span> {profile.customer["Address"] || "—"}</div>
              <a data-testid="customer-drive-link" href={profile.customer["Drive Folder URL"]} target="_blank" rel="noopener noreferrer"
                className="col-span-2 inline-flex items-center gap-1 text-weld-glow hover:underline">
                <ExternalLink size={14} /> Open Google Drive folder
              </a>
            </div>

            <div>
              <Area label="Internal Notes" value={notes} onChange={e => setNotes(e.target.value)} />
              <Button data-testid="save-notes-btn" size="sm" variant="outline" className="mt-2" onClick={saveNotes}><Save size={14} /> Save Notes</Button>
            </div>

            {[
              ["Leads", profile.leads, r => `${r["Lead ID"]} — ${r["Requested Service"] || "General"} (${r["Lead Status"]})`],
              ["Estimates", profile.estimates, r => `${r["Estimate Number"]} — $${r["Total Estimate"]} (${r["Estimate Status"]})`],
              ["Jobs", profile.jobs, r => `${r["Job ID"]} — ${r["Job Title"]} (${r["Job Status"]})`],
              ["Payments", profile.payments, r => `${r["Payment ID"]} — $${r["Total Paid"]} ${r["Payment Type"]} (${r["Payment Method"]})`],
              ["Follow-Ups", profile.followUps, r => `${r["Follow-Up ID"]} — ${r["Follow-Up Type"]} ${fmtDate(r["Follow-Up Date"])} (${r["Status"]})`],
              ["Warranties", profile.warranties, r => `${r["Warranty ID"]} — expires ${fmtDate(r["Warranty Expiration Date"])} (${r["Warranty Status"]})`],
            ].map(([title, list, line]) => (
              <div key={title}>
                <h4 className="text-weld-glow text-xs uppercase tracking-wider mb-2">{title} ({list.length})</h4>
                {list.length === 0
                  ? <p className="text-white/30">None</p>
                  : <ul className="space-y-1">{list.map((r, i) => <li key={i} className="text-white/70 bg-white/5 rounded-lg px-3 py-2">{line(r)}</li>)}</ul>}
              </div>
            ))}

            <div>
              <h4 className="text-weld-glow text-xs uppercase tracking-wider mb-2">Photos ({profile.photos.length})</h4>
              <div className="grid grid-cols-3 gap-2">
                {profile.photos.map(p => (
                  <a key={p["Media ID"]} href={p["Website URL"] || p["Drive URL"]} target="_blank" rel="noopener noreferrer" className="relative group">
                    <img src={p["Website URL"]} alt={p["Project Name"]} className="w-full aspect-square object-cover rounded-lg border border-white/10" />
                    <span className="absolute bottom-1 left-1"><Pill tone="blue">{p["Category"]}</Pill></span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
